import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaAngleLeft,
  FaAngleRight,
  FaBarcode,
  FaBox,
  FaFileUpload,
  FaPlus,
  FaSearch,
  FaSync,
  FaTag,
  FaTimes,
} from "react-icons/fa";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./CProductList.css";

import { productActions } from "../../sagas/productSlice";
import { categoryActions } from "../../sagas/categorySlice";
import { formatPrice } from "../../helpers/utility";
import { CBL_PAGE, CPMT_PRODUCTS_FORM_NEW, CPMT_PRODUCTS_PAGE, CPMT_RECEPTION_PAGE, CPMT_STOCK_PAGE, PAY_PAGE, POS_PAGE, RETOUR_FORM_NEW, RETOUR_PAGE } from "../../routes/routeConstants";
import POSHeader from "./POSHeader";
import POSKeypadModal from "./POSKeypadModal";
import POSProductFormModal from "./POSProductFormModal";

const CProductList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const searchInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const { list: categories } = useSelector((state) => state.categories || {});
  const unites = useSelector((state) => state.products?.unites || []);

  const {
    products,
    loading,
    totalElements,
    totalPages,
    pageSize,
  } = useSelector((state) => state.products || {});

  const { role, businessName, fullName, commercialId, pointOfSaleId } = useSelector((state) => state.auth || {});
  const isAdmin = role === "ROLE_ADMIN";

  const [activeTab, setActiveTab] = useState("PRODUCTS");

  const [currentPage, setCurrentPage] = useState(1);

  const [filters, setFilters] = useState({
    categorieId: null,
    query: "",
  });
  const [editingProduct, setEditingProduct] = useState(null);

  // POS UI state
  const [showKeypad, setShowKeypad] = useState(false);
  const [categoryModal, setCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [productModal, setProductModal] = useState(false);

  const categoryOptions = useMemo(() => {
    return (categories || []).map((c) => ({
      value: c.id,
      label: c.libelle,
    }));
  }, [categories]);

  const getProductFormPath = () => CPMT_PRODUCTS_FORM_NEW;

  // --- LOAD INITIAL DATA ---
  useEffect(() => {
    dispatch(categoryActions.list({ query: "" }));
    dispatch(productActions.findAllUnites());
  }, [dispatch]);

  // --- FETCH PRODUCTS ---
  useEffect(() => {
    dispatch(
      productActions.findAllArticles({
        ...filters,
        page: currentPage - 1,
        size: 10,
      })
    );
  }, [dispatch, currentPage, filters.categorieId]);

  // --- Search manual trigger (keypad / enter) ---
  const doSearch = useCallback(() => {
    dispatch(
      productActions.findAllArticles({
        ...filters,
        page: 0,
        size: 10,
      })
    );
    setCurrentPage(1);
  }, [dispatch, filters]);

  // --- F2 focus ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "F2") {
        e.preventDefault();
        setShowKeypad(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // --- CATEGORY APPLY ---
  const applyCategory = useCallback(
    (cat) => {
      setSelectedCategory(cat || null);
      setFilters((prev) => ({ ...prev, categorieId: cat?.value || null }));
      setCurrentPage(1);
      setCategoryModal(false);
    },
    []
  );

  // --- PAGINATION ---
  const handlePageChange = (page) => {
    if (!totalPages) return;
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // --- Upload file ---
  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    if (!allowedTypes.includes(file.type)) {
      Swal.fire({
        icon: "error",
        title: t("product.invalidFileType"),
        text: t("product.excelOnly"),
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);

      dispatch(
        productActions.uploadProducts({
          formData,
          onSuccess: () => {
            Swal.fire({
              icon: "success",
              title: t("product.uploadSuccess"),
              confirmButtonText: "OK",
              confirmButtonColor: "#198754",
            });
            doSearch();
          },
        })
      );
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const refreshStock = () => {
    setIsRefreshing(true);

    dispatch(
      productActions.updateGlobalStock({
        onSuccess: () => {
          doSearch();
          setIsRefreshing(false);
        },
        onError: () => setIsRefreshing(false),
      })
    );
  };

  // --- TABLE VIEW ---
  const TableView = () => (
    <div className="pos-table-wrap">
      <Table hover bordered className="align-middle mb-0 pos-table">
        <thead>
          <tr>
            <th style={{ width: 180 }}>
              <FaBox className="me-2 text-primary" size={12} />
              {t("product.category")}
            </th>

            <th style={{ width: 140 }}>
              <FaBarcode className="me-2 text-primary" size={12} />
              {t("product.code")}
            </th>

            <th style={{ width: 160 }}>
              <FaBarcode className="me-2 text-primary" size={12} />
              {t("product.barcode")}
            </th>

            <th>
              <FaBox className="me-2 text-primary" size={12} />
              {t("product.description")}
            </th>

            <th style={{ width: 120 }}>
              {t("devis.unite")}
            </th>

            <th className="text-end" style={{ width: 140 }}>
              <FaTag className="me-2 text-primary" size={12} />
              {t("product.salePrice")}
            </th>

            <th className="text-end" style={{ width: 140 }}>
              <FaTag className="me-2 text-primary" size={12} />
              {t("product.buyingPrice")}
            </th>

            <th className="text-end" style={{ width: 120 }}>
              <FaTag className="me-2 text-primary" size={12} />
              {t("product.stockDisponible")}
            </th>

            <th className="text-center" style={{ width: 120 }}>
              {t("client.active")}
            </th>
            <th style={{ width: 90 }}></th>
          </tr>
        </thead>

        <tbody>
          {!products || products.length === 0 ? (
            <tr>
              <td colSpan="9" className="text-center py-4">
                <div className="text-muted">{t("product.noProducts")}</div>
              </td>
            </tr>
          ) : (
            products.map((p) => (
              <tr key={p.id} className="pos-row">
                <td className="pos-cell">{p.categorieLibelle || "-"}</td>
                <td className="pos-cell fw-bold">{p.code || "-"}</td>
                <td className="pos-cell">{p.codeBarre || "-"}</td>
                <td className="pos-cell text-uppercase">{p.libelle}</td>
                <td className="pos-cell">
                  {unites.find((u) => u.code === p.unite)?.libelle || p.unite || "-"}
                </td>

                <td className="pos-cell text-end fw-bold">{formatPrice(p.prixVenteTTC1)}</td>
                <td className="pos-cell text-end">{formatPrice(p.prixAchatTTC)}</td>

                <td className="pos-cell text-end">
                  {Number(p.stockDisponible || 0) <= 0 ? (
                    <Badge color="danger" pill>
                      0
                    </Badge>
                  ) : Number(p.stockDisponible) < 5 ? (
                    <Badge color="warning" pill>
                      {p.stockDisponible}
                    </Badge>
                  ) : (
                    <Badge color="success" pill>
                      {p.stockDisponible}
                    </Badge>
                  )}
                </td>

                <td className="pos-cell text-center">
                  {p.actif ? <Badge color="success">Actif</Badge> : <Badge color="danger">Bloqué</Badge>}
                </td>
                <td className="pos-cell text-center">
                  <Button
                    size="sm"
                    color="primary"
                    onClick={() => {
                      setEditingProduct(p);
                      setProductModal(true);
                    }}
                  >
                    ✏️
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </div >
  );

  return (
    <Container fluid className="pos-page">
      <POSHeader
        businessName={businessName || "MATJARPOS"}
        fullName={fullName}
        commercialId={commercialId}
        pointOfSaleId={pointOfSaleId}
        isAdmin={isAdmin}
        activeTab={activeTab}
        onChangeTab={(tab) => {
          setActiveTab(tab);
          if (tab === "VENTES") navigate(POS_PAGE);
          if (tab === "STOCK") navigate(CPMT_STOCK_PAGE);
          if (tab === "PRODUCTS") navigate(CPMT_PRODUCTS_PAGE);
          if (tab === "AVANCE") navigate(PAY_PAGE);
          if (tab === "RETOURS") navigate(RETOUR_FORM_NEW);
          if (tab === "RECEPTION") navigate(CPMT_RECEPTION_PAGE);
        }}
      />

      <Row>
        <Col md={12}>
          <Card className="pos-card">
            <CardBody className="pos-card-body">
              {/* LOADING */}
              {loading ? (
                <div className="pos-loading">
                  <Spinner color="primary" />
                  <div className="mt-2 text-muted">{t("common.loading")}</div>
                </div>
              ) : (
                <>
                  {/* ======= POS TOOLBAR ======= */}
                  <div className="pos-toolbar">
                    {/* Title */}
                    <div className="pos-toolbar-title">
                      <span className="pos-title-badge">ARTICLES</span>
                      <small className="text-muted">{totalElements || 0} éléments</small>
                    </div>

                    {/* Search + category + actions */}
                    <div className="pos-toolbar-controls">
                      {/* Search */}
                      <div
                        className="pos-search-bar"
                        onClick={() => setShowKeypad(true)}
                        role="button"
                        tabIndex={0}
                      >
                        <FaSearch className="pos-search-icon" />
                        <Input
                          innerRef={searchInputRef}
                          type="text"
                          value={filters.query}
                          readOnly
                          inputMode="none"
                          onKeyDown={(e) => e.preventDefault()}
                          placeholder={t("product.searchPlaceholder")}
                          className="pos-search-input pos-nokeyboard"
                        />

                        {filters.query ? (
                          <Button
                            color="light"
                            className="pos-search-clear"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilters((prev) => ({ ...prev, query: "" }));
                              setCurrentPage(1);
                              doSearch();
                            }}
                          >
                            <FaTimes />
                          </Button>
                        ) : (
                          <div className="pos-search-hint">F2</div>
                        )}
                      </div>

                      {/* Category */}
                      <Button
                        color="light"
                        className="pos-filter-btn"
                        onClick={() => setCategoryModal(true)}
                        title="Catégorie"
                      >
                        <FaBox className="me-2" />
                        {selectedCategory?.label || "Catégorie"}
                      </Button>

                      {/* Actions */}
                      <div className="pos-actions-mini">
                        {fullName?.includes('AZIZ') && <Button
                          color="primary"
                          className="pos-icon-btn"
                          onClick={() => setProductModal(true)}
                          title={t("product.new")}
                        >
                          <FaPlus />
                        </Button>}

                        <Button
                          color="success"
                          className="pos-icon-btn"
                          onClick={refreshStock}
                          title={t("product.refreshStock")}
                          disabled={isRefreshing}
                        >
                          <FaSync className={isRefreshing ? "pos-spin" : ""} />
                        </Button>

                        {/* <Button
                          color="secondary"
                          className="pos-icon-btn"
                          onClick={() => fileInputRef.current?.click()}
                          title={t("product.uploadProducts")}
                        >
                          <FaFileUpload />
                        </Button>

                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileUpload}
                          accept=".xlsx,.xls"
                          style={{ display: "none" }}
                        /> */}
                      </div>
                    </div>
                  </div>

                  {/* ======= Pagination Compact ======= */}
                  <div className="pos-pagination">
                    <div className="pos-page-info">
                      <strong>{currentPage}</strong> / {totalPages || 1}
                      <span className="ms-2 text-muted">
                        ({pageSize || 10}/page)
                      </span>
                    </div>

                    <div className="pos-page-actions">
                      <Button
                        color="light"
                        className="pos-page-btn"
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                      >
                        <FaAngleDoubleLeft />
                      </Button>

                      <Button
                        color="light"
                        className="pos-page-btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <FaAngleLeft />
                      </Button>

                      <Button
                        color="light"
                        className="pos-page-btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= (totalPages || 1)}
                      >
                        <FaAngleRight />
                      </Button>

                      <Button
                        color="light"
                        className="pos-page-btn"
                        onClick={() => handlePageChange(totalPages || 1)}
                        disabled={currentPage >= (totalPages || 1)}
                      >
                        <FaAngleDoubleRight />
                      </Button>
                    </div>
                  </div>

                  {/* ======= TABLE ======= */}
                  <TableView />
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* =============================
          CATEGORY MODAL (POS)
      ============================= */}
      <Modal isOpen={categoryModal} toggle={() => setCategoryModal(false)} centered>
        <ModalHeader toggle={() => setCategoryModal(false)}>
          Choisir une catégorie
        </ModalHeader>

        <ModalBody>
          <div className="d-grid gap-2">
            <Button
              color={!selectedCategory ? "primary" : "light"}
              className="py-3 fw-bold"
              onClick={() => applyCategory(null)}
            >
              Tous
            </Button>

            {categoryOptions.map((c) => (
              <Button
                key={c.value}
                color={selectedCategory?.value === c.value ? "primary" : "light"}
                className="py-3 fw-bold"
                onClick={() => applyCategory(c)}
              >
                {c.label}
              </Button>
            ))}
          </div>
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={() => setCategoryModal(false)}>
            Fermer
          </Button>
        </ModalFooter>
      </Modal>

      {/* =============================
          SEARCH KEYPAD MODAL
      ============================= */}
      <POSKeypadModal
        isOpen={showKeypad}
        toggle={() => setShowKeypad(false)}
        value={filters.query}
        onChange={(val) => {
          setFilters((prev) => ({ ...prev, query: (val || "").toUpperCase() }));
        }}
        onSearch={() => {
          doSearch();
          setShowKeypad(false);
        }}
        title={`Recherche Articles`}
      />

      <Modal
        isOpen={productModal}
        toggle={() => setProductModal(false)}
        size="xl"
        centered
        className="pos-product-modal"
      >
        <ModalHeader toggle={() => { setProductModal(false); setEditingProduct(null); }}>
          {editingProduct ? "Modifier produit" : "Nouveau produit"}
        </ModalHeader>

        <ModalBody style={{ maxHeight: "80vh", overflowY: "auto" }}>
          <POSProductFormModal
            onClose={() => {
              setProductModal(false);
              setEditingProduct(null);
              doSearch(); // refresh list after save
            }}
            categories={categories}
            unites={unites}
            product={editingProduct}
          />
        </ModalBody>
      </Modal>
    </Container>
  );
};

export default CProductList;
